import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import CreateNewMarkingCriteriaDesktop from '../CreateNewMarkingCriteriaDesktop';
import { isMobileView } from '../../ReactiveRender';
import CriteriaContainer from '../CriteriaContainer';
import {
  createNewMarkingCriteria,
  getAllMarkingCriteria,
  updateMarkingCriteria,
  deleteMarkingCriteria,
  getDefaultCriteria,
  getNewCriteria,
} from '../../../service';
import Loader from '../../Loader';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toast from '../../Toast';
import { useMarkingCriterias } from '../../state/hooks';
import { validateRubric } from '../../../components2/markingCriteria';
import useNavigationBlock from '../../../hooks/useNavigationBlock';
import GeneralPopup from '../../GeneralPopup';
import { useProfile } from '../../state/hooks';
import { useUserStates } from '../../../hooks/useUserStates';
import { buildMetadata } from '../../../utils/subjects';
import { useSubjects } from '../../../hooks/useSubjects';

const READ_ONLY_RUBRIC_MESSAGE =
  'You can’t edit this rubric. Duplicate it from the Rubrics list to make your own version.';

// The backend refuses (403) an edit of a rubric the teacher doesn't own.
const rubricSaveErrorMessage = (error, fallback) =>
  error?.status === 403 ? READ_ONLY_RUBRIC_MESSAGE : fallback;

export default function CreateNewMarkingCriteriaRoot(props) {
  // React Router keeps this route mounted when only :markingCriteriaId changes
  // (back/forward between two rubrics, an edited URL), but the editor loads its
  // rubric only when the rubric list changes. A fresh editor per id stops the
  // previous rubric's content, isUpdating and unsaved-changes state carrying
  // over, where Save would write that rubric's content to this id.
  const { markingCriteriaId } = useParams();
  return <MarkingCriteriaEditor key={markingCriteriaId} {...props} />;
}

function MarkingCriteriaEditor(props) {
  const {
    setIsNavigationBlocked,
    isShowNavigationBlockPopup,
    confirmNavigationChange,
    cancelNavigationPopup,
  } = useNavigationBlock();
  const { markingCriteriaId } = useParams();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [markingCriteria, setMarkingCriteria] = useState(getDefaultCriteria);
  // The rubric exactly as it was loaded, kept so validation can tell a duplicate
  // level name the teacher just introduced (block) from one this rubric was
  // already published with (warn). Null while creating: everything is new then.
  const savedMarkingCriteria = React.useRef(null);
  const history = useHistory();
  const queryClient = useQueryClient();

  /**
   * After a rubric edit, invalidate BOTH caches that feed marking-tool views:
   *
   *   - ['markingCriterias']                          legacy cache used by
   *                                                   useMarkingCriterias /
   *                                                   AccountSettingRoot
   *   - ['feedbackTool', 'items', 'markingCriteria']  new cache used by
   *   - ['feedbackTool', 'itemsCount', 'markingCriteria']  FeedbackToolWrapper
   *                                                   (the actual list on
   *                                                   /tools/rubrics) via
   *                                                   useFeedbackTool
   *
   * Without the feedbackTool invalidations, the editor's `setMarkingCriterias`
   * call updated the LEGACY cache but the list pulled from the unchanged
   * feedbackTool cache, so previewing the just-edited rubric showed stale
   * content until a hard refresh. The Comment Banks / Success Criteria /
   * Short Answer editors already do this — they just used to be the only
   * editors that did.
   */
  const invalidateMarkingToolCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['markingCriterias'] });
    queryClient.invalidateQueries({ queryKey: ['markingCriteriasWithPreferences'] });
    queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'markingCriteria'] });
    queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'markingCriteria'] });
  };
  const mobileView = isMobileView();
  const { data: profile } = useProfile();
  const { states: userStates } = useUserStates();
  const { rawSubjects: subjectsList } = useSubjects(userStates);

  const {
    data: markingCriterias,
    isLoadingdata: isLoadingMarkingCriterias,
    setData: setMarkingCriterias,
    resetData: resetMarkingCriterias,
  } = useMarkingCriterias();

  React.useEffect(() => {
    if (markingCriteriaId === 'new') {
      setIsLoading(false);
    }
    if (markingCriterias) {
      const loadedMarkingCriteria = markingCriterias.filter(
        (criteria) => criteria.id === markingCriteriaId,
      );
      if (loadedMarkingCriteria.length > 0) {
        const loaded = loadedMarkingCriteria[0];
        // Extract fields from metadata if not present at root level
        const metadata = loaded.metadata || {};
        const withLevelIds = {
          ...loaded,
          years: loaded.years?.length ? loaded.years : (metadata.years || []),
          subjects: metadata.subjects?.length ? metadata.subjects : (loaded.subjects || []),
          taskTypes: loaded.taskTypes?.length ? loaded.taskTypes : (metadata.taskTypes || []),
          responseTypes: loaded.responseTypes?.length ? loaded.responseTypes : (metadata.responseTypes || []),
          criterias: (loaded.criterias || []).map((criteria, cIdx) => ({
            ...criteria,
            levels: (criteria.levels || []).map((level, lIdx) => ({
              ...level,
              id: level.id || `loaded-${cIdx}-${lIdx}`,
            })),
          })),
        };
        setMarkingCriteria(withLevelIds);
        // Snapshot the loaded shape (post level-id backfill, so criteria keys
        // match what validation sees) BEFORE any edit touches it.
        savedMarkingCriteria.current = withLevelIds;
        setIsUpdating(true);
        setIsLoading(false);
      }
    }
  }, [markingCriterias]);

  function addCriteria() {
    const newCriteria = getNewCriteria(markingCriteria.criterias.length);
    setMarkingCriteria({
      ...markingCriteria,
      criterias: [...markingCriteria.criterias, newCriteria],
    });
  }

  function deleteCriteria(criteriaId) {
    const newCriterias = markingCriteria.criterias.filter((criteria, index) => {
      return index != criteriaId;
    });
    setMarkingCriteria({ ...markingCriterias, criterias: newCriterias });
  }

  const addLevel = (criteriaId) => {
    const newLevel = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: '',
      description: '',
    };
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === criteriaId) {
        return {
          ...criteria,
          levels: [...criteria.levels, newLevel],
        };
      }
      return criteria;
    });
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const addLevelInBetween = (criteriaId, levelId) => {
    const newLevel = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: '',
      description: '',
    };
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === criteriaId) {
        const newLevels = [...criteria.levels];
        newLevels.splice(levelId + 1, 0, newLevel);
        return {
          ...criteria,
          levels: newLevels,
        };
      }
      return criteria;
    });
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const deleteLevel = (criteriaId, levelId) => {
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === criteriaId) {
        return {
          ...criteria,
          levels: criteria.levels.filter((level, index) => {
            return index != levelId;
          }),
        };
      }
      return criteria;
    });
    if (newCriterias[criteriaId].levels.length === 0) {
      newCriterias.splice(criteriaId, 1);
    }
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const saveMarkingCriteria = (settings = {}) => {
    // A Jeddle template is read-only; the editor hides its save controls.
    if (isUpdating && markingCriteria.isSystem) {
      toast(<Toast message={READ_ONLY_RUBRIC_MESSAGE} preserveCase />);
      return;
    }
    const years = settings.years || markingCriteria.years || [];
    const subjects = settings.subjects || markingCriteria.subjects || [];
    if (years.length === 0 || subjects.length === 0) {
      toast(<Toast message="Please select at least one Year and one Subject" />);
      return;
    }
    // Only grandfather duplicates against the snapshot if it is a snapshot of
    // THIS rubric: the ref outlives a route change from an existing rubric to
    // /new, and a new rubric must have nothing grandfathered.
    const saved =
      savedMarkingCriteria.current?.id === markingCriteriaId
        ? savedMarkingCriteria.current
        : null;
    if (validateRubric(markingCriteria, saved)) {
      const taskTypes = settings.taskTypes || markingCriteria.taskTypes || (markingCriteria.taskType ? [markingCriteria.taskType] : []);
      const responseTypes = settings.responseTypes || markingCriteria.responseTypes || (markingCriteria.responseType ? [markingCriteria.responseType] : []);
      const metadata = buildMetadata(userStates, {
        years,
        subjects,
        taskTypes,
        responseTypes,
      }, subjectsList);

      const newMarkingCriteria = {
        title: markingCriteria.title,
        type: 'RUBRICS',
        criterias: markingCriteria.criterias.map((criteria) => {
          return {
            title: criteria.title,
            selectedLevel: '',
            levels: criteria.levels.map((level) => {
              return {
                name: level.name,
                description: level.description,
              };
            }),
          };
        }),
        years,
        taskTypes,
        responseTypes,
        metadata,
      };
      const action = isUpdating
        ? updateMarkingCriteria(newMarkingCriteria, markingCriteriaId)
        : createNewMarkingCriteria(newMarkingCriteria);
      // updateMarkingCriteria rejects on failure and leaves the toast to us.
      const onSaveFailed = (error) => {
        console.error('Error saving rubric:', error);
        toast(
          <Toast
            message={rubricSaveErrorMessage(
              error,
              'Couldn’t save the rubric. Check your connection and try again.'
            )}
            preserveCase
          />
        );
      };
      action
        .then((response) => {
          // createNewMarkingCriteria resolves null on failure, after its own
          // error toast.
          if (!isUpdating && !response) return;
          if (isUpdating) {
            const updatedMarkingCriterias = markingCriterias.map(
              (markingCriteria, index) => {
                if (markingCriteria.id === markingCriteriaId) {
                  return {
                    ...markingCriteria,
                    ...newMarkingCriteria,
                  };
                }
                return markingCriteria;
              },
            );
            setMarkingCriterias(updatedMarkingCriterias);
          } else {
            resetMarkingCriterias();
          }
          // Without this, the rubric list on /tools/rubrics keeps showing
          // the pre-edit version because it reads from a separate React
          // Query cache key (['feedbackTool', 'items', 'markingCriteria'])
          // that setMarkingCriterias doesn't touch.
          invalidateMarkingToolCaches();
          setIsNavigationBlocked(false);

          toast(
            <Toast
              message={
                isUpdating
                  ? 'Rubric updated'
                  : 'Rubric created'
              }
            />,
          );
          if (isUpdating) {
            history.push(`/tools/rubrics/${markingCriteriaId}`);
          } else {
            const newId = response?.id?.value ?? response?.id;
            history.push(newId ? `/tools/rubrics/${newId}` : '/tools/rubrics');
          }
        }, onSaveFailed)
        .catch((error) => {
          console.error('Error:', error);
        });
    } else {
      return;
    }
  };

  const deleteMarkingCriteriaMethod = () => {
    if (markingCriteria.isSystem) {
      toast(<Toast message={'Cannot delete template rubrics'} />);
      return;
    }
    
    deleteMarkingCriteria(markingCriteriaId)
      .then(() => {
        toast(<Toast message={'Rubrics deleted'} />);
        window.localStorage.setItem('markingCriteria', 'true');
        history.push('/settings');
      })
      .catch((error) => {
        toast(
          <Toast
            message={'An error occured while deleting rubrics'}
          />,
        );
      });
  };

  const handleTitleChange = (event) => {
    setMarkingCriteria({ ...markingCriteria, title: event.target.value });
  };

  const saveTitleOnly = async (newTitle) => {
    if (!isUpdating || markingCriteria.isSystem || !newTitle.trim()) {
      return;
    }

    try {
      // Send full marking criteria with updated title
      const updatedMarkingCriteria = {
        ...markingCriteria,
        title: newTitle.trim(),
        criterias: markingCriteria.criterias.map((criteria) => ({
          title: criteria.title,
          selectedLevel: criteria.selectedLevel || '',
          levels: criteria.levels.map((level) => ({
            name: level.name,
            description: level.description,
          })),
        })),
      };

      await updateMarkingCriteria(updatedMarkingCriteria, markingCriteriaId);

      const updatedMarkingCriterias = markingCriterias.map(
        (criteria) => {
          if (criteria.id === markingCriteriaId) {
            return {
              ...criteria,
              title: newTitle.trim(),
            };
          }
          return criteria;
        },
      );
      setMarkingCriterias(updatedMarkingCriterias);
      // saveTitleOnly hits the same stale-cache trap as the main save —
      // the legacy ['markingCriterias'] cache reflects the rename but
      // FeedbackToolWrapper's cache does not. Invalidate both.
      invalidateMarkingToolCaches();
      toast(<Toast message="Rubrics updated" />);
    } catch (error) {
      console.error('Error updating title:', error);
      toast(
        <Toast
          message={rubricSaveErrorMessage(error, 'Error updating title')}
          preserveCase
        />
      );
    }
  };

  const updateCriteriaTitle = (id, newTitle) => {
    setIsNavigationBlocked(true);
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === id) {
        return {
          ...criteria,
          title: newTitle,
        };
      }
      return criteria;
    });
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const updateLevelName = (criteriaId, levelId, newName) => {
    setIsNavigationBlocked(true);
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === criteriaId) {
        return {
          ...criteria,
          levels: criteria.levels.map((level, index) => {
            if (index === levelId) {
              return {
                ...level,
                name: newName,
              };
            }
            return level;
          }),
        };
      }
      return criteria;
    });
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const updateLevelDescription = (criteriaId, levelId, newDescription) => {
    setIsNavigationBlocked(true);
    if (newDescription.length > 500) {
      return;
    }
    const newCriterias = markingCriteria.criterias.map((criteria, index) => {
      if (index === criteriaId) {
        return {
          ...criteria,
          levels: criteria.levels.map((level, index) => {
            if (index === levelId) {
              return {
                ...level,
                description: newDescription,
              };
            }
            return level;
          }),
        };
      }
      return criteria;
    });
    setMarkingCriteria({ ...markingCriteria, criterias: newCriterias });
  };

  const criterias = markingCriteria.criterias.map((criteria, index) => {
    return (
      <CriteriaContainer
        key={index}
        criteriaId={index}
        addLevel={addLevel}
        deleteLevel={deleteLevel}
        deleteCriteria={deleteCriteria}
        criteria={criteria}
        updateCriteriaTitle={updateCriteriaTitle}
        updateLevelName={updateLevelName}
        updateLevelDescription={updateLevelDescription}
      />
    );
  });

  if (isLoading) {
    return (
      <>
        <Loader />
      </>
    );
  }

  return (
    <>
      {isShowNavigationBlockPopup && (
        <GeneralPopup
          hidePopup={cancelNavigationPopup}
          title="Unsaved Changes"
          textContent="You have unsaved changes in your template. If you leave this page, your changes will be lost. Do you want to continue?"
          buttonText="Leave Without Saving"
          confirmButtonAction={confirmNavigationChange}
          closeBtnText="Stay on Page"
          cancelPopup={cancelNavigationPopup}
        />
      )}
      <CreateNewMarkingCriteriaDesktop
        key={markingCriteriaId}
        {...{
          criterias,
          addCriteria,
          addLevel,
          deleteLevel,
          addLevelInBetween,
          deleteCriteria,
          updateCriteriaTitle,
          updateLevelName,
          updateLevelDescription,
          saveMarkingCriteria,
          deleteMarkingCriteriaMethod,
          handleTitleChange,
          saveTitleOnly,
          isUpdating,
          markingCriteria,
          markingCriteriaId,
        }}
      />
    </>
  );
}
